import { useEffect, useRef } from 'react';
import type { ViewportInfo } from '../../hooks/useFabricCanvas';
import type { GridConfig } from '../../types/image-editor.types';
import { tickPositions, visibleSceneRange } from '../../lib/ruler-ticks';

interface GridProps {
  viewport: ViewportInfo;
  config: GridConfig;
  /** When true, ruler chrome is present and we need to offset
   *  the grid's start by RULER_THICKNESS so it lines up with the
   *  canvas content. When false, grid spans the full wrapper. */
  rulersVisible: boolean;
}

/** Distance (CSS px) the rulers reserve at the top + left of the
 *  wrapper. Kept in sync with the constant in `Rulers.tsx`. */
const RULER_THICKNESS = 22;

/** Full-canvas measurement-grid overlay. Renders major lines at
 *  `config.size` scene-space intervals with `config.subdivisions`
 *  light minor lines between them. Color comes from `config`;
 *  minors render at 30% opacity for visual hierarchy.
 *
 *  Distinct from CanvasArea's CSS transparency checkerboard —
 *  that's the underlay that shows through transparent pixels;
 *  this is a pixel-aligned ruler-axis grid for layout work. */
export function Grid({ viewport, config, rulersVisible }: GridProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    drawGrid(canvasRef.current, viewport, config, rulersVisible);
  }, [viewport, config, rulersVisible]);

  // pointer-events: none so the grid never intercepts clicks
  // meant for the Fabric canvas or other overlays.
  const offset = rulersVisible ? RULER_THICKNESS : 0;
  const w = Math.max(0, viewport.viewportWidth - offset);
  const h = Math.max(0, viewport.viewportHeight - offset);
  return (
    <canvas
      ref={canvasRef}
      width={w}
      height={h}
      className="absolute pointer-events-none z-[5]"
      style={{
        left: offset,
        top: offset,
        width: w,
        height: h,
      }}
      aria-hidden="true"
    />
  );
}

function drawGrid(
  canvas: HTMLCanvasElement | null,
  viewport: ViewportInfo,
  config: GridConfig,
  rulersVisible: boolean,
): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  if (viewport.zoom <= 0 || config.size <= 0) return;

  const offset = rulersVisible ? RULER_THICKNESS : 0;
  // visibleSceneRange's `panPx` argument is the screen-pixel
  // offset of scene-(0,0) within the drawable area. When rulers
  // are on, that drawable area starts at `RULER_THICKNESS`, so
  // subtract before passing in.
  const xRange = visibleSceneRange(viewport.panX - offset, w, viewport.zoom);
  const yRange = visibleSceneRange(viewport.panY - offset, h, viewport.zoom);

  // Minor subdivision lines. Subdivisions = 1 means major-only;
  // higher values add `subdivisions - 1` light lines between
  // each major pair.
  const subdivisions = Math.max(1, Math.floor(config.subdivisions));
  if (subdivisions > 1) {
    const minorSize = config.size / subdivisions;
    ctx.strokeStyle = hexWithOpacity(config.color, 0.3);
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const sx of tickPositions(xRange.min, xRange.max, minorSize)) {
      const screen = sx * viewport.zoom + viewport.panX - offset + 0.5;
      ctx.moveTo(screen, 0);
      ctx.lineTo(screen, h);
    }
    for (const sy of tickPositions(yRange.min, yRange.max, minorSize)) {
      const screen = sy * viewport.zoom + viewport.panY - offset + 0.5;
      ctx.moveTo(0, screen);
      ctx.lineTo(w, screen);
    }
    ctx.stroke();
  }

  // Major lines on top of the minor ones.
  ctx.strokeStyle = hexWithOpacity(config.color, 0.7);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const sx of tickPositions(xRange.min, xRange.max, config.size)) {
    const screen = sx * viewport.zoom + viewport.panX - offset + 0.5;
    ctx.moveTo(screen, 0);
    ctx.lineTo(screen, h);
  }
  for (const sy of tickPositions(yRange.min, yRange.max, config.size)) {
    const screen = sy * viewport.zoom + viewport.panY - offset + 0.5;
    ctx.moveTo(0, screen);
    ctx.lineTo(w, screen);
  }
  ctx.stroke();
}

/** Convert `#rrggbb` + alpha into the rgba() string canvas2d
 *  accepts. Handles short-form `#rgb` too. Returns the input
 *  unchanged if it doesn't look like a hex color. */
function hexWithOpacity(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const raw = match[1];
  const expanded =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
